import { useCallback, useRef } from "react";
import { analyticsTracker, type TrackEvent } from "./analyticsTracker";

type ImpressionData = Omit<TrackEvent, "at" | "name">;

const VISIBLE_MS = 1000;

/** Один общий observer на все карточки: ≥50% в viewport ≥1 c → listing_impression. */
let observer: IntersectionObserver | null = null;
const data = new WeakMap<Element, { get: () => ImpressionData; timer: ReturnType<typeof setTimeout> | null }>();

function getObserver(): IntersectionObserver | null {
  if (observer || typeof IntersectionObserver === "undefined") return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const d = data.get(entry.target);
        if (!d) continue;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!d.timer) {
            d.timer = setTimeout(() => {
              d.timer = null;
              analyticsTracker.impression(d.get());
              observer?.unobserve(entry.target);
            }, VISIBLE_MS);
          }
        } else if (d.timer) {
          clearTimeout(d.timer);
          d.timer = null;
        }
      }
    },
    { threshold: [0, 0.5] },
  );
  return observer;
}

/**
 * Ref для корня карточки объявления. Не влияет на рендер: данные читаются
 * в момент события через ref, наблюдение — один общий IntersectionObserver.
 */
export function useImpressionRef(impression: ImpressionData | null) {
  const latest = useRef(impression);
  latest.current = impression;
  const current = useRef<Element | null>(null);

  return useCallback((el: Element | null) => {
    try {
      const obs = getObserver();
      if (current.current && current.current !== el) {
        const prev = data.get(current.current);
        if (prev?.timer) clearTimeout(prev.timer);
        obs?.unobserve(current.current);
        data.delete(current.current);
      }
      current.current = el;
      if (!el || !obs || !latest.current) return;
      data.set(el, { get: () => latest.current as ImpressionData, timer: null });
      obs.observe(el);
    } catch {
      /* трекинг не должен ломать карточку */
    }
  }, []);
}
